import { Form } from 'antd';
import { LoginPayload } from '@/services/authentication-service/authentication.service.types';
import { Button } from '../button/button';
import styles from './sign-in-form.module.scss';
import TextField from '../form/text-field/text-field';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

export type FieldType = {
  email: string;
  password: string;
};

interface SignInFormProps {
  onSubmit: (values: FieldType) => void;
  errors?: string[] | string;
  isLoading?: boolean;
}

export function SignInForm({ onSubmit, errors, isLoading }: SignInFormProps) {
  const [showError, setShowError] = useState(!!errors);
  const [emailErrors, setEmailErrors] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (errors) {
      setShowError(!!errors);
    }
  }, [errors]);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      onFieldsChange={() => {
        setShowError(false);
        setEmailErrors(!!form.getFieldError('email')[0]);
      }}
      autoComplete="off"
      className={styles.formContainer}
      requiredMark="optional"
      layout="vertical"
    >
      <Form.Item<FieldType>
        label="Email address"
        name="email"
        htmlFor="email-input"
        rules={[
          { required: true, message: 'Please input your email!' },
          {
            type: 'email',
            message: 'This is not an valid E-mail!',
          },
        ]}
      >
        <TextField
          status={showError || emailErrors ? 'error' : undefined}
          data-testid="email"
          id="email-input"
          type="email"
          autoComplete="email"
          onPaste={(e) => {
            const pastedValue = e.clipboardData.getData('Text');
            form.setFieldsValue({ email: pastedValue.trim() });
            e.preventDefault();
          }}
        />
      </Form.Item>

      <Form.Item<LoginPayload>
        label="Password"
        name="password"
        htmlFor="password-input"
        className={styles.passwordContainer}
        rules={[{ required: true, message: 'Please input your password!' }]}
        shouldUpdate
      >
        <TextField
          status={showError ? 'error' : undefined}
          data-testid="password"
          type="password"
        />
      </Form.Item>
      <Link to="/forgot-password" className={styles.link}>
        Forgot Password
      </Link>

      <div className={styles.errorContainer}>
        {showError && (
          <span>
            {typeof errors === 'string' ? (
              <p className={styles.error}>{errors}</p>
            ) : (
              errors?.map((error, index) => (
                <p key={index} className={styles.error}>
                  {error}
                </p>
              ))
            )}
          </span>
        )}
      </div>

      <Form.Item className={styles.submitButton}>
        <Button
          size="large"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          block
          disabled={showError}
        >
          Sign In
        </Button>
      </Form.Item>
    </Form>
  );
}
